import React from 'react';
import { MainLayout } from '../MainLayout/MainLayout';
import { Layers, Database, ShoppingBag, Receipt, CreditCard, Activity, Cpu, RotateCcw, AlertTriangle } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/Badge';
import { mockSapSimulatorService } from '@/services/mock/MockSapSimulatorService';

interface SAPLayoutProps {
  children: React.ReactNode;
}

export const SAPLayout: React.FC<SAPLayoutProps> = ({ children }) => {
  const config = mockSapSimulatorService.getSimulationConfig();
  const isScenarioActive = config.activeScenario !== 'SUCCESS';

  const sapTabs = [
    { title: 'Dashboard', href: ROUTES.SAP.DASHBOARD, icon: Activity },
    { title: 'Material Master', href: ROUTES.SAP.MATERIALS, icon: Database },
    { title: 'Vendor Master', href: ROUTES.SAP.VENDORS, icon: Database },
    { title: 'Masters (Org & Budgets)', href: ROUTES.SAP.MASTERS, icon: Layers },
    { title: 'PR (ME51N)', href: ROUTES.SAP.PR, icon: Layers },
    { title: 'PO (ME21N)', href: ROUTES.SAP.PO, icon: ShoppingBag },
    { title: 'GRN (MIGO)', href: ROUTES.SAP.GRN, icon: Layers },
    { title: 'Invoice (MIRO)', href: ROUTES.SAP.INVOICES, icon: Receipt },
    { title: 'Payment (F110)', href: ROUTES.SAP.PAYMENTS, icon: CreditCard },
    { title: 'Integration Logs', href: ROUTES.SAP.LOGS, icon: RotateCcw },
    { title: 'Simulation Control', href: ROUTES.SAP.CONTROL, icon: Cpu },
  ];

  return (
    <MainLayout>
      <div className="space-y-6 font-heading">
        {/* Environment Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border pb-4 gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-extrabold tracking-tight text-foreground">SAP S/4HANA Enterprise Simulator Portal</h1>
              <Badge variant="sap" className="font-mono text-[10px] font-bold">
                SIMULATOR — DEVELOPMENT
              </Badge>
              {isScenarioActive && (
                <span className="inline-flex items-center gap-1 bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 px-2 py-0.5 rounded text-[10px] font-bold animate-pulse">
                  <AlertTriangle className="h-3 w-3" /> Failure Injected: {config.activeScenario}
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-secondary font-medium mt-1">
              Simulated SAP ERP environment for validating ME51N PRs, ME21N POs, MIGO GRNs, MIRO 3-way matching, and failure testing.
            </p>
          </div>
        </div>

        {/* SAP Sub Navigation Bar */}
        <div className="flex overflow-x-auto border-b border-border space-x-1.5 custom-scrollbar pb-0.5">
          {sapTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.href}
                to={tab.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 whitespace-nowrap border-b-2 py-2.5 px-3 text-xs font-bold transition-all',
                    isActive
                      ? 'border-sap text-sap dark:text-sky-400 bg-sky-500/5 rounded-t-xl'
                      : 'border-transparent text-foreground-secondary hover:text-foreground hover:bg-muted/50 rounded-t-xl'
                  )
                }
              >
                <Icon className="h-4 w-4" />
                <span>{tab.title}</span>
              </NavLink>
            );
          })}
        </div>

        <div>{children}</div>
      </div>
    </MainLayout>
  );
};
